import {getTestProps} from '@benchling/js-test-utils';
import {Button, Group, Modal, Stack, Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import navigateToURL from 'lib/navigateToURL';
import buildListItemViewModelFromAgent from 'llm/chat-library/agents/buildListItemViewModelFromAgent';
import buildListItemViewModelFromExample from 'llm/chat-library/agents/buildListItemViewModelFromExample';
import ChatSettingsListingView from 'llm/chat-library/shared/list-detail/ChatSettingsListingView';
import type {ListGroup, ListItemViewModel} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import {AgentsGroup} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import {
  buildCopyAgentFormState,
  COPY_AGENT_CHAT_SETTINGS_EXAMPLE_ID,
  CopyAgentCreateBanner,
} from 'llm/routines/routineAgentCopy';
import {getSelectableRunAsModes} from 'llm/routines/routineAuthorization';
import RoutineDetailPanel, {DetailPanelMode, RoutineDetailTab} from 'llm/routines/RoutineDetailPanel';
import {ROUTINE_EXAMPLES, RoutineExample} from 'llm/routines/routineExamples';
import {
  archiveRoutine as archiveRoutineApi,
  pauseRoutine as pauseRoutineApi,
  resumeRoutine as resumeRoutineApi,
  runRoutine as runRoutineApi,
} from 'llm/routines/RoutinesDAO';
import {Routine, RoutineRunAs, RoutineStatus, TriggerType} from 'llm/routines/RoutineTypes';
import {RoutineFormState} from 'llm/routines/useRoutineForm';
import useRoutines from 'llm/routines/useRoutines';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import PreviewBadge from 'ui-library/badges/PreviewBadge';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runModal, {RunModalProps} from 'ui-library/runModal';
import {getUserMessage} from 'util/Net';

function isCurrentUser(userId: string): boolean {
  return EntityStore.currentUser() ? EntityStore.currentUser().id === userId : false;
}

function ConfirmPauseAgentModal({close}: RunModalProps<boolean>): JSX.Element {
  const handleCancel = useCallback(() => close(false), [close]);
  const handleConfirm = useCallback(() => close(true), [close]);

  return (
    <Stack spacing="md">
      <Text size="sm" {...getTestProps({component: 'ConfirmPauseAgentModal', element: 'warning'})}>
        This agent is running as its creator. If you pause the agent, only its creator can resume it.
      </Text>
      <Group position="right" spacing="xs">
        <Button variant="ghost" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="warn" size="sm" onClick={handleConfirm}>
          Pause
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * The Background agents tab's group tabs, in display order. `id` is the group tab value; `label` is
 * the tab text. The listing view derives the horizontal tab bar from these.
 *
 * Each group sources its rows differently (see `itemsByGroup` in the component): "All" lists the
 * tenant's real agents, while "Example agents" lists read-only, pre-configured templates that seed
 * the create form when clicked. Active vs. paused isn't a group — it's surfaced through each real
 * agent row's toggle.
 */
const AGENTS_GROUPS: Array<{id: AgentsGroup; label: string}> = [
  {id: AgentsGroup.ALL, label: 'All'},
  {id: AgentsGroup.MINE, label: 'My agents'},
  {id: AgentsGroup.SHARED, label: 'Shared with me'},
  {id: AgentsGroup.EXAMPLE_AGENTS, label: 'Example agents'},
];

/**
 * The Background agents tab in the chat settings modal. It mirrors the Skills tab: a grouped
 * listing view backed by {@link useRoutines}, with a detail panel (the shared routine create/edit
 * form) shown when an agent is selected (via the URL's `selectedItemId`) or when creating a new one.
 *
 * "Agents" are modeled in code as "routines"; this reuses the routines domain layer (query, DAO,
 * detail form) while presenting them through the new modal's shared list/detail primitives.
 */
export default function ChatSettingsAgentContent({
  selectedItemId,
  group,
  initiallyCreating = false,
  initialDetailTab,
  onSelectItem,
  onGroupChange,
  onBack,
  onCloseSettings,
}: {
  selectedItemId: string | null;
  group: AgentsGroup;
  initiallyCreating?: boolean;
  initialDetailTab?: RoutineDetailTab;
  onSelectItem: (id: string) => void;
  onGroupChange: (group: AgentsGroup) => void;
  /** Returns to the listing (clears the selected agent). */
  onBack: () => void;
  /** Closes the entire AI settings modal. */
  onCloseSettings: () => void;
}): JSX.Element {
  const timeBasedEnabled = BenchlingConfig.LLM_ENABLE_ROUTINES_TIME_BASED;
  const eventBasedEnabled = BenchlingConfig.LLM_ENABLE_ROUTINES_EVENT_BASED;

  const {routines, isLoading, isError, refetch} = useRoutines();
  const createDisabled = getSelectableRunAsModes().length === 0;
  const [isCreating, setIsCreating] = useState(initiallyCreating && !createDisabled);
  // The example a user clicked in the "Example agents" group, opened as a prefilled create form. Null
  // for the plain "Add" (blank) create path.
  const [createExample, setCreateExample] = useState<RoutineExample | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Routine | null>(null);
  // Optimistic pause/resume statuses keyed by agent id, applied over the query results until the
  // mutation and its follow-up refetch settle.
  const [statusOverrides, setStatusOverrides] = useState<Record<string, RoutineStatus>>({});
  const pendingToggleIds = useRef(new Set<string>());

  // Only surface agents whose trigger type is enabled for this tenant.
  const enabledTriggerTypes = useMemo(() => {
    const types = new Set<TriggerType>();
    if (timeBasedEnabled) {
      types.add(TriggerType.TIME_BASED);
    }
    if (eventBasedEnabled) {
      types.add(TriggerType.EVENT_BASED);
    }
    return types;
  }, [timeBasedEnabled, eventBasedEnabled]);

  const visibleRoutines = useMemo(
    () =>
      routines
        .filter((r) => enabledTriggerTypes.has(r.triggerType))
        .map((r) => {
          const overrideStatus = statusOverrides[r.id];
          return overrideStatus != null ? {...r, status: overrideStatus} : r;
        }),
    [routines, enabledTriggerTypes, statusOverrides]
  );

  // Examples are gated by trigger type exactly like real agents, so a disabled trigger type never
  // seeds the create form with a config the tenant can't use.
  const visibleExamples = useMemo(
    () =>
      ROUTINE_EXAMPLES.filter((example) => {
        const {triggerType} = example.formState;
        return triggerType != null && enabledTriggerTypes.has(triggerType);
      }),
    [enabledTriggerTypes]
  );

  // When the tenant has no agents yet, land on the "Example agents" group instead of an empty "All"
  // list so the user has a starting point. This runs once per mount and only while the group is still
  // the default (ALL), so it never fights a user who later clicks back to "All" to view the (empty)
  // list. It waits for the listing to load successfully so the count is accurate: a failed query maps
  // to an empty list too, and we don't want a transient API error to silently redirect the user.
  const hasAppliedEmptyDefault = useRef(false);
  useEffect(() => {
    if (hasAppliedEmptyDefault.current || isLoading || isError) {
      return;
    }
    hasAppliedEmptyDefault.current = true;
    if (group === AgentsGroup.ALL && visibleRoutines.length === 0 && visibleExamples.length > 0) {
      onGroupChange(AgentsGroup.EXAMPLE_AGENTS);
    }
  }, [isLoading, isError, group, visibleRoutines.length, visibleExamples.length, onGroupChange]);

  // Run the DAO mutation and refetch the listing.
  const runMutation = useCallback(
    async (mutation: () => Promise<unknown>) => {
      await mutation();
      await refetch();
    },
    [refetch]
  );

  const handleToggleActive = useCallback(
    async (routine: Routine, active: boolean) => {
      if (!active && routine.runAs === RoutineRunAs.CREATOR && !isCurrentUser(routine.creatorId)) {
        const confirmed = await runModal(
          ConfirmPauseAgentModal,
          {},
          {title: 'Pause agent?', centered: true, zIndex: ZINDEX_FLOATING_UI}
        );
        if (confirmed !== true) {
          return;
        }
      }
      // Pending state lives here rather than in the row, so a row remounted by navigating to
      // details and back can't start an opposite toggle while the first one is in flight.
      if (pendingToggleIds.current.has(routine.id)) {
        return;
      }
      pendingToggleIds.current.add(routine.id);
      const clearOverride = (): void => {
        pendingToggleIds.current.delete(routine.id);
        setStatusOverrides(({[routine.id]: _cleared, ...rest}) => rest);
      };
      setStatusOverrides((prev) => ({
        ...prev,
        [routine.id]: active ? RoutineStatus.ACTIVE : RoutineStatus.PAUSED,
      }));
      try {
        await (active ? resumeRoutineApi(routine.id) : pauseRoutineApi(routine.id));
      } catch (e) {
        clearOverride();
        const message = getUserMessage(e);
        if (message == null) {
          throw e;
        }
        notifications.warn(message);
        return;
      }
      try {
        await refetch();
      } finally {
        clearOverride();
      }
    },
    [refetch]
  );

  // Run a saved agent immediately and, on success, surface a toast linking to the new chat. A
  // failure (e.g. hitting the tenant execution rate limit) surfaces the server's message as an
  // error toast, matching the legacy RoutinesPage behavior.
  const handleRun = useCallback(async (routine: Routine) => {
    try {
      const {chatSessionId} = await runRoutineApi(routine.id);
      notifications.success('Your Agent is running.', {
        actionText: 'View chat',
        onActionClick: () => {
          openChat(chatSessionId);
        },
      });
    } catch (e) {
      const message = getUserMessage(e);
      // Re-throw unexpected errors (those without a user-facing message) rather than swallowing
      // them. Known server errors surface as a toast.
      if (message == null) {
        throw e;
      }
      notifications.warn(message);
    }
  }, []);

  const handleArchiveRequest = useCallback((routine: Routine) => {
    setArchiveTarget(routine);
  }, []);

  const closeArchiveModal = useCallback(() => {
    setArchiveTarget(null);
  }, []);

  const handleConfirmArchive = useCallback(async () => {
    const target = archiveTarget;
    setArchiveTarget(null);
    if (target == null) {
      return;
    }
    await runMutation(() => archiveRoutineApi(target.id, 'Retired'));
    // If the archived agent was the one open in the detail panel, return to the listing.
    if (selectedItemId === target.id) {
      onBack();
    }
  }, [archiveTarget, runMutation, selectedItemId, onBack]);

  const handleCloseDetail = useCallback(() => {
    setIsCreating(false);
    setCreateExample(null);
    onBack();
  }, [onBack]);

  // Rows in the "Example agents" group aren't real agents, so a click opens the create form prefilled
  // from the example rather than routing to the (non-existent) edit-by-id path used for real agents.
  const handleSelectItem = useCallback(
    (id: string) => {
      if (group === AgentsGroup.EXAMPLE_AGENTS) {
        if (createDisabled) {
          return;
        }
        // Resolve against the trigger-type-filtered examples (the ones actually rendered) so a
        // disabled example can never seed the create form.
        const example = visibleExamples.find((e) => e.id === id);
        if (example != null) {
          setCreateExample(example);
          setIsCreating(true);
        }
        return;
      }
      onSelectItem(id);
    },
    [createDisabled, group, onSelectItem, visibleExamples]
  );

  const handlers = useMemo(
    () => ({
      onToggleActive: handleToggleActive,
      onRun: handleRun,
      onArchive: handleArchiveRequest,
      onViewDetails: (routine: Routine) => {
        handleSelectItem(routine.id);
      },
    }),
    [handleToggleActive, handleRun, handleArchiveRequest, handleSelectItem]
  );

  const handleCopyRequest = useCallback(
    (formState: RoutineFormState, sourceRoutineId: string) => {
      if (createDisabled) {
        return;
      }
      navigateToURL(chatSettingsModalUrl(ChatSettingsTab.AGENTS));
      const copiedState = buildCopyAgentFormState(formState, sourceRoutineId);
      setCreateExample({
        id: COPY_AGENT_CHAT_SETTINGS_EXAMPLE_ID,
        label: COPY_AGENT_CHAT_SETTINGS_EXAMPLE_ID,
        icon: 'robot',
        formState: {
          ...copiedState,
          banner: <CopyAgentCreateBanner sourceName={formState.name} runAs={copiedState.runAs} />,
        },
      });
      setIsCreating(true);
    },
    [createDisabled]
  );

  const listItemById = useMemo(() => {
    const map: Record<string, ListItemViewModel> = {};
    visibleRoutines.forEach((r) => {
      map[r.id] = buildListItemViewModelFromAgent(r, handlers);
    });
    return map;
  }, [visibleRoutines, handlers]);

  // Show the create/edit form when creating a new agent or when one is selected via the URL. The
  // detail panel fetches the selected agent by id itself, so it works even before the list loads.
  if (isCreating || selectedItemId != null) {
    const createMode: DetailPanelMode =
      createExample != null
        ? {type: 'create', example: createExample.formState, exampleId: createExample.id}
        : {type: 'create'};
    return (
      <RoutineDetailPanel
        key={isCreating ? `create-${createExample?.id ?? 'blank'}` : `edit-${selectedItemId}`}
        mode={isCreating ? createMode : {type: 'edit', routineId: selectedItemId ?? ''}}
        onClose={handleCloseDetail}
        onAgentHomepageNavigate={onCloseSettings}
        onMutationSuccess={refetch}
        timeBasedEnabled={timeBasedEnabled}
        eventBasedEnabled={eventBasedEnabled}
        initialEditTab={initialDetailTab}
        onCopyRequest={handleCopyRequest}
      />
    );
  }

  // Each group sources its rows differently: real agents (with toggles/menus) vs. read-only example
  // templates. Keyed by group so adding a group is a compile-time-checked, one-line addition.
  const itemsByGroup: Record<AgentsGroup, Array<ListItemViewModel>> = {
    [AgentsGroup.ALL]: visibleRoutines.map((r) => listItemById[r.id]),
    [AgentsGroup.MINE]: visibleRoutines
      .filter((r) => isCurrentUser(r.creatorId))
      .map((r) => listItemById[r.id]),
    [AgentsGroup.SHARED]: visibleRoutines
      .filter((r) => !isCurrentUser(r.creatorId))
      .map((r) => listItemById[r.id]),
    [AgentsGroup.EXAMPLE_AGENTS]: visibleExamples.map((example) =>
      buildListItemViewModelFromExample(example, createDisabled)
    ),
  };

  const groups: Array<ListGroup> = AGENTS_GROUPS.map(({id, label}) => ({
    id,
    header: label,
    items: itemsByGroup[id],
  }));

  const addButton = (
    <Button
      variant="primary"
      size="xs"
      leftIcon={<BenchlingIcon icon="plus" />}
      disabled={createDisabled}
      onClick={() => {
        setCreateExample(null);
        setIsCreating(true);
      }}
      {...getTestProps({component: 'ChatSettingsAgentContent', element: 'addAgentButton'})}
    >
      Add
    </Button>
  );

  return (
    <>
      <ChatSettingsListingView
        header={{
          title: 'Agents',
          titleBadge: <PreviewBadge />,
          action: addButton,
        }}
        groups={groups}
        activeGroupId={group}
        onGroupChange={(value) => {
          onGroupChange(value as AgentsGroup);
        }}
        isLoading={isLoading}
        emptyMessage="No agents yet."
        selectedItemId={selectedItemId}
        onSelectItem={handleSelectItem}
      />
      <Modal
        opened={archiveTarget != null}
        onClose={closeArchiveModal}
        title="Archive Agent"
        centered={true}
        closeOnClickOutside={true}
        zIndex={ZINDEX_FLOATING_UI}
      >
        <Stack spacing="md">
          <Text size="sm">Are you sure? This will stop and remove this Agent.</Text>
          <Group position="right" spacing="xs">
            <Button variant="ghost" size="sm" onClick={closeArchiveModal}>
              Cancel
            </Button>
            <Button
              variant="warn"
              size="sm"
              onClick={handleConfirmArchive}
              {...getTestProps({component: 'ChatSettingsAgentContent', element: 'confirmArchiveButton'})}
            >
              Archive
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
